import { io } from "socket.io-client";

/**
 * Connects the patient portal directly to the EMR media server so patients can
 * chat with real support agents (the same server that powers /support/chat in
 * the EMR frontend).
 *
 * Protocol (derived from the EMR agent client, services/agent-portal-socket.js):
 *  - connect with query { userId: patientId, token, type: 'patient' }
 *  - emit 'register' { userId }
 *  - room id for a patient<->agent pair is `${patientId}_${agentId}`
 *  - patient -> agent: emit 'private-message' { toUserId, roomId, message, senderType: 'patient', ... }
 *  - agent  -> patient: receive 'private-message' { agentId, agentName, message, senderType: 'agent', ... }
 *  - history: emit 'get-chat-history' { currentUserId, withUserId }
 *
 * The patient emits 'request-agent' { patient, patientId, issue }; the media
 * server broadcasts 'patient-waiting' to every online agent so the patient
 * appears in their Waiting Queue (EMR /support/chat).
 */

const MEDIA_SERVER_URI =
  process.env.REACT_APP_MEDIA_SERVER_URL || "https://ai.dedahospital.com";

const REQUEST_AGENT_EVENT = "request-agent";

export function mediaRoomId(patientId, agentId) {
  return `${patientId}_${agentId}`;
}

export function createAgentBridge({ patientId, token, patient }) {
  let socket = null;
  let currentAgentId = null;
  const pendingMessages = [];
  const listeners = {
    message: [],
    status: [],
  };

  const emitStatus = (status, extra = {}) => {
    listeners.status.forEach((cb) => cb({ status, ...extra }));
  };

  const connect = () => {
    if (socket) return socket;

    socket = io(MEDIA_SERVER_URI, {
      transports: ["websocket"],
      path: "/media/socket.io",
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      query: {
        userId: `${patientId}`,
        token,
        type: "patient",
      },
    });

    socket.on("connect", () => {
      socket.emit("register", { userId: `${patientId}` }, () => { });
      emitStatus("connected");
    });

    socket.on("disconnect", () => emitStatus("disconnected"));
    socket.on("connect_error", () => emitStatus("error"));

    // Agent replies (and, on some servers, agent pickup notifications).
    socket.on("private-message", (data) => {
      if (!data) return;

      // Only surface messages authored by the agent side.
      const fromAgent = data.senderType === "staff" || data.senderType === "agent" || data.senderType === "doctor" || (data.agentId && !data.patientId);
      if (!fromAgent) return;

      const newlyAssigned = data.agentId && currentAgentId !== `${data.agentId}`;
      if (data.agentId) {
        currentAgentId = `${data.agentId}`;
      }

      listeners.message.forEach((cb) =>
        cb({
          message: data.message,
          agentId: data.agentId ? `${data.agentId}` : currentAgentId,
          agentName: data.agentName || "Support Agent",
          timestamp: data.timestamp || new Date().toISOString(),
        }),
      );

      if (data.agentId) {
        emitStatus("agent_connected", {
          agentId: `${data.agentId}`,
          agentName: data.agentName || "Support Agent",
        });
      }

      if (newlyAssigned) {
        flushPending();
      }
    });

    // Explicit pickup signal, if the server sends one.
    socket.on("agent-assigned", (data) => {
      if (data?.agentId) {
        currentAgentId = `${data.agentId}`;
        emitStatus("agent_connected", {
          agentId: `${data.agentId}`,
          agentName: data.agentName || "Support Agent",
        });
        flushPending();
      }
    });

    return socket;
  };

  // Announce to agents that this patient needs help (enters the waiting queue).
  const requestAgent = (issue) => {
    if (!socket) connect();

    const send = () => {
      socket.emit(
        REQUEST_AGENT_EVENT,
        {
          patient: patient || { id: patientId },
          patientId: `${patientId}`,
          issue: issue || "Needs assistance",
          token,
        },
        (res) => {
          // Server reports how many agents were notified (0 = none online).
          if (res && res.success && res.onlineAgents === 0) {
            emitStatus("no_agents");
          }
        },
      );
      emitStatus("waiting");
    };

    if (socket.connected) {
      send();
    } else {
      socket.once("connect", send);
    }
  };

  const patientDisplayName = () =>
    patient
      ? [patient.other_names, patient.surname].filter(Boolean).join(" ") || patient.name
      : undefined;

  const deliverMessage = (message) => {
    const roomId = mediaRoomId(patientId, currentAgentId);
    socket.emit("private-message", {
      toUserId: `${currentAgentId}`,
      roomId,
      message,
      senderType: "patient",
      fromUserId: `${patientId}`,
      patientId: `${patientId}`,
      patientName: patientDisplayName(),
    });
  };

  const flushPending = () => {
    if (!socket || !currentAgentId) return;
    while (pendingMessages.length > 0) {
      deliverMessage(pendingMessages.shift());
    }
  };

  // Send a patient message to the assigned agent. Returns true if delivered
  // immediately, false if buffered until an agent picks up.
  const sendMessage = (message) => {
    if (!socket) return false;

    if (!currentAgentId) {
      pendingMessages.push(message);
      return false;
    }

    deliverMessage(message);
    return true;
  };

  const loadHistory = (agentId, cb) => {
    if (!socket) return;
    socket.emit(
      "get-chat-history",
      { currentUserId: `${patientId}`, withUserId: `${agentId}` },
      (res) => cb(Array.isArray(res) ? res : []),
    );
  };

  const onMessage = (cb) => {
    listeners.message.push(cb);
    return () => {
      listeners.message = listeners.message.filter((fn) => fn !== cb);
    };
  };

  const onStatus = (cb) => {
    listeners.status.push(cb);
    return () => {
      listeners.status = listeners.status.filter((fn) => fn !== cb);
    };
  };

  const hasAgent = () => Boolean(currentAgentId);

  const disconnect = () => {
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
    currentAgentId = null;
  };

  return {
    connect,
    requestAgent,
    sendMessage,
    loadHistory,
    onMessage,
    onStatus,
    hasAgent,
    disconnect,
  };
}
